import React, { useCallback } from "react";
import { Form, SubmitButton, Input, Select } from "formik-antd";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { newConnectionSchema } from "/app/src/schemas";
import { useTranslation } from "react-i18next";
import { handleSubmissionErrors } from "/app/src/helpers/forms";
import { Connection } from "/app/src/models";
import TypeField from "./typeField";
import Box from "/app/src/components/generic/components/box";
import { settingService } from "/app/src/services";
import NewFormikTypeWrapper from "./connectionType/newFormikTypeWrapper";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

interface FormValues {
  name: string | undefined;
  type: string | undefined;
  url: string | undefined;
  authentication: string | undefined;
  username: string | undefined;
  password: string | undefined;
  token: string | undefined;
  login: string | undefined;
  tokenDuration: string | undefined;
  tokenDurationUnit: string | undefined;
  tokenKey: string | undefined;
}

/**
 * Creates a new connection form component.
 *
 * @param {object} toggleNew - Function to toggle the new connection form visibility.
 * @param {function} addConnection - Function to add a new connection, returns a promise with a connection object.
 * @returns {JSX.Element} - The rendered new connection form.
 */
export default function NewConnection({
  toggleNew,
  addConnection,
}: {
  toggleNew: () => void;
  addConnection: (
    connection: Connection,
  ) => Promise<{ connection: Connection }>;
}) {
  const { t } = useTranslation();

  /**
   * Retrieves authentication options based on the specified connection type.
   *
   * @param {string} type - The type of connection (e.g., REST, GraphQL).
   * @returns {Array} - An array of authentication options with value and label properties.
   */
  const getAuthOptions = useCallback((type: string) => {
    if (type === "REST") {
      return [
        { value: "Basic", label: "Basic" },
        { value: "No Auth", label: "No Auth" },
        { value: "OAuth1", label: "OAuth 1.0" },
        { value: "OAuth2", label: "OAuth 2.0" },
        {
          value: "TBA",
          label: "Token Based Authentication",
        },
        {
          value: "Cookie",
          label: "Cookie Based Authentication",
        },
        {
          value: "hmac",
          label: "HMAC",
        },
      ];
    }
    if (type === "GraphQL") {
      return [
        { value: "No Auth", label: "No Auth" },
        { value: "Basic", label: "Basic" },
        {
          value: "TBA",
          label: "Token Based Authentication",
        },
      ];
    }
    if (type === "MSSQL" || type === "IBM2") {
      return [{ value: "Password", label: "Username/Password" }];
    }
    return [{ value: "No Auth", label: "No Auth" }];
  }, []);

  /**
   * Renders the new connection form.
   *
   * @param {Object} props - The formik props including dirty, isValid, and values.
   * @returns {JSX.Element} - The new connection form component.
   */
  const newConnectionForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, values }) => (
        <Form layout="vertical">
          <Row>
            <Col span={22}>
              <h3>
                {t("translation:create")} {t("translation:new_connection")}
              </h3>
            </Col>
            <Col span={2}>
              <IconBuilder
                icon="Close"
                color="#e00000"
                size={14}
                onClick={toggleNew}
                className="float-right"
              />
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label={t("translation:name")}>
                <Input suffix name="name" className="user" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <TypeField />
            </Col>
            <Col span={12}>
              <Form.Item
                name="authentication"
                label={t("translation:authentication")}
              >
                <Select
                  name="authentication"
                  size="large"
                  disabled={values.type === undefined}
                >
                  {getAuthOptions(values.type).map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="url" label={t("translation:address")}>
                <Input suffix name="url" className="url" size="large" />
              </Form.Item>
            </Col>
            <NewFormikTypeWrapper />
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={24}>
              <SubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || !isValid}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [getAuthOptions, t, toggleNew],
    );
  const initialFormValues: FormValues = {
    username: "",
    password: "",
    url: "",
    name: "",
    type: "REST",
    token: "",
    authentication: "",
    login: "",
    tokenDuration: "",
    tokenDurationUnit: "h",
    tokenKey: "token",
  };
  /**
   * Handles the creation of a new connection when the form is submitted.
   *
   * @param {FormValues} values - The values from the new connection form.
   * @param {Object} actions - Formik actions for handling form submission errors.
   * @returns {Promise<void>} - A promise that resolves when the connection is created.
   */
  const createConnectionHandler = useCallback(
    async (values: FormValues, actions) => {
      await addConnection({
        name: values.name,
        type: values.type,
        url: values.url,
        authentication: values.authentication,
        username: values.username,
        password: values.password,
        token: values.token,
      })
        .then((resp) => {
          if (["TBA", "OAuth2"].includes(values.authentication)) {
            settingService.createSingle({
              connectionId: resp.connection.id,
              type: "tokenDuration",
              value: `${values.tokenDuration}${values.tokenDurationUnit}`,
            });
            settingService.createSingle({
              connectionId: resp.connection.id,
              type: "login",
              value: values.login,
            });
            settingService.createSingle({
              connectionId: resp.connection.id,
              type: "tokenKey",
              value: values.tokenKey,
            });
          }
          toggleNew();
        })
        .catch((errors) => {
          handleSubmissionErrors(errors, actions.setFieldError);
        });
    },
    [addConnection, toggleNew],
  );

  return (
    <Box>
      <Formik
        component={newConnectionForm}
        validationSchema={newConnectionSchema}
        initialValues={initialFormValues}
        enableReinitialize
        onSubmit={createConnectionHandler}
      />
    </Box>
  );
}
